import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, styled, type Theme } from "@mui/material/styles";

const consensusAccent = (theme: Theme) =>
  theme.palette.mode === "dark" ? theme.palette.gold : theme.palette.yellow;

export const Root = styled(Box)({
  width: 360,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: 360,
  overflowY: "auto",
});

export const CapBanner = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
  padding: theme.spacing(1, 1.5),
  color: theme.palette.yellow.dark,
  fontSize: 13,
  fontWeight: 500,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const GroupHeader = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$selected",
})<{ $selected: boolean }>(({ $selected, theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  cursor: "pointer",
  userSelect: "none",
  backgroundColor: $selected
    ? alpha(theme.palette.gold.main, 0.16)
    : "transparent",

  "&:hover": {
    backgroundColor: $selected
      ? alpha(theme.palette.gold.main, 0.24)
      : alpha(theme.palette.text.primary, 0.03),
  },
}));

export const GroupLabel = styled(Typography)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const HeaderRight = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
}));

export const ArrowIcon = styled(KeyboardArrowDownIcon, {
  shouldForwardProp: (prop) => prop !== "$open",
})<{ $open: boolean }>(({ $open, theme }) => ({
  fontSize: 16,
  color: theme.palette.text.disabled,
  transform: `rotate(${$open ? -180 : 0}deg)`,
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shorter,
  }),
}));

export const SelectedIcon = styled(CheckIcon)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.yellow.dark,
}));

export const GroupBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  backgroundColor: alpha(theme.palette.text.primary, 0.03),
}));

export const PlatformRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$selected",
})<{ $selected: boolean }>(({ $selected, theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.875, 1),
  margin: theme.spacing(0, 0, 0.5, 1),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  backgroundColor: $selected
    ? alpha(theme.palette.yellow.main, 0.28)
    : "transparent",

  "&:hover": {
    backgroundColor: $selected
      ? alpha(theme.palette.yellow.main, 0.36)
      : alpha(theme.palette.text.primary, 0.07),
  },
}));

export const PlatformDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>(({ $color }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: $color,
  flexShrink: 0,
}));

export const PlatformLabel = styled(Typography)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  color: theme.palette.text.primary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const PlatformCheck = styled(Box)(({ theme }) => ({
  display: "flex",
  color: theme.palette.yellow.dark,
  "& .MuiSvgIcon-root": { fontSize: 16 },
}));

export const ConsensusOption = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$selected",
})<{ $selected: boolean }>(({ $selected, theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  margin: theme.spacing(0.5, 0, 0.5, 1),
  padding: theme.spacing(0.875, 1),
  borderRadius: theme.shape.borderRadius,
  border: `1px dashed ${consensusAccent(theme).dark}`,
  color: consensusAccent(theme).dark,
  fontSize: 13,
  cursor: "pointer",
  backgroundColor: $selected
    ? alpha(
        consensusAccent(theme).main,
        theme.palette.mode === "dark" ? 0.16 : 0.35
      )
    : "transparent",

  "&:hover": {
    backgroundColor: alpha(
      consensusAccent(theme).main,
      theme.palette.mode === "dark" ? 0.24 : 0.25
    ),
  },
}));

export const ConsensusCheck = styled(Box)(({ theme }) => ({
  display: "flex",
  color: consensusAccent(theme).dark,
  "& .MuiSvgIcon-root": { fontSize: 16 },
}));
